import { useState } from 'react';
import { Newspaper, ArrowUpRight, Calendar } from 'lucide-react';

function Updates() {
  const [activeFilter, setActiveFilter] = useState('All');
  const filters = ['All', 'FBC200', 'Alumni News', 'Opportunities'];

  const updatesList = [
    {
      id: 1,
      category: 'FBC200',
      title: 'FBC Alumni Float Parade Marks the Journey to 200',
      desc: 'Alumni, students, staff, and well-wishers joined a colourful procession from Old Fourah Bay College, Cline Town, to Siaka Stevens Stadium as part of the build-up to FBC\'s bicentenary celebration.',
      image: '/images/fbc-float-parade.jpg',
      date: 'May 23, 2026',
      readTime: '3 min read'
    },
    {
      id: 2,
      category: 'FBC200',
      title: 'Alumni and FBC Management Unite Ahead of Bicentennial',
      desc: 'FBC alumni met with college leadership and the Bicentennial Anniversary Planning Committee to align alumni activities, resource mobilisation, and preparations for the historic 200-year celebration.',
      image: '/images/fbc-alumni-meeting.jpg',
      date: 'May 18, 2026',
      readTime: '4 min read'
    },
    {
      id: 3,
      category: 'Opportunities',
      title: 'Innovation Seed Fund Opens Opportunity for Recent FBC Graduates',
      desc: 'Fourah Bay College, in partnership with Oxford University Africa Society and Africell Impact Foundation, opened an innovation funding opportunity for eligible recent FBC graduates.',
      image: '/images/fbc-innovation-seed-fund.jpg',
      date: 'March 18, 2026',
      readTime: '5 min read'
    },
    {
      id: 4,
      category: 'Alumni News',
      title: 'Global Alumni Chapters Announce Joint Support Fund',
      desc: 'Chapters across the UK, North America, and Sierra Leone have launched a unified fund to supply campus sanitation refurbishments and library materials ahead of the bicentenary.',
      image: '/images/fbc-campus-freetown.jpg',
      date: 'January 10, 2026',
      readTime: '3 min read'
    },
    {
      id: 5,
      category: 'FBC200',
      title: 'Bicentenary Celebration Schedule Officially Released',
      desc: 'The official schedule of events for the Fourah Bay College Bicentenary (FBC200) has been published, detailing academic conferences, alumni dinners, and community celebrations.',
      image: '/images/fbc-celebration.jpg',
      date: 'November 15, 2025',
      readTime: '6 min read'
    }
  ];

  const filteredUpdates = updatesList.filter(item => {
    if (activeFilter === 'All') return true;
    if (activeFilter === 'FBC200' && item.category === 'FBC200') return true;
    if (activeFilter === 'Alumni News' && item.category === 'Alumni News') return true;
    if (activeFilter === 'Opportunities' && item.category === 'Opportunities') return true;
    return false;
  });

  return (
    <div className="min-h-screen bg-stone-50 font-sans pb-20">

      {/* Premium Hero Header */}
      <div className="bg-white border-b border-stone-200 pt-16 pb-12 mb-10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 text-fbc-green text-[10px] font-black uppercase tracking-widest mb-4">
            <Newspaper className="w-3.5 h-3.5" /> FBC NEWSROOM
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-4 tracking-tight">Updates & Announcements</h1>
          <p className="text-gray-500 text-lg max-w-2xl leading-relaxed">
            Stay informed with the latest news, announcements, stories, and activities from Fourah Bay College and our global alumni network.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6">
        {/* Category Filters */}
        <div className="flex gap-2 mb-8 border-b border-stone-200 overflow-x-auto pb-px">
          {filters.map(filter => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`px-5 py-3 text-sm font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${
                activeFilter === filter
                  ? 'border-fbc-green text-fbc-green font-bold'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {filter}
            </button>
          ))}
        </div>

        {/* Updates Grid/List */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {filteredUpdates.map(post => (
            <div
              key={post.id}
              className="bg-white rounded-2xl overflow-hidden border border-stone-200 shadow-sm hover:shadow-md transition-all group cursor-pointer flex flex-col"
            >
              <div className="h-48 overflow-hidden bg-stone-100 relative">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=600&q=80';
                  }}
                />
                <span className="absolute top-4 left-4 bg-fbc-green text-white text-xs font-semibold px-3 py-1 uppercase tracking-wider shadow-sm">
                  {post.category}
                </span>
              </div>

              <div className="p-6 flex-grow flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 text-xs text-gray-400 mb-3 font-medium">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5 text-fbc-green" />
                      {post.date}
                    </span>
                    <span>•</span>
                    <span>{post.readTime}</span>
                  </div>
                  
                  <h3 className="text-lg font-bold text-gray-900 mb-3 leading-snug group-hover:text-fbc-green transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  
                  <p className="text-gray-600 text-sm mb-6 leading-relaxed line-clamp-3">
                    {post.desc}
                  </p>
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    alert(`Opening full article: "${post.title}"...`);
                  }}
                  className="text-sm font-semibold text-fbc-green flex items-center gap-1 group-hover:underline w-fit"
                >
                  Read full article <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredUpdates.length === 0 && (
          <div className="text-center py-20 bg-white rounded-2xl border border-stone-250">
            <Newspaper className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-1">No updates found</h3>
            <p className="text-gray-500 text-sm">There are no updates in this category at the moment.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Updates;
